import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subscription } from 'rxjs';
import * as _ from 'underscore';
import { Enum, KeyPair } from '../../../../models/base';
import { PayrollService } from '../../../../services/payroll.service';
import { PeopleService } from '../../../../services/people.service';
import { SharedService } from '../../../../services/shared.service';
import { PayrollManagement } from '../../../../tools/payroll-management';

@Component({
  selector: 'add-person-to-payroll',
  templateUrl: './add-person-to-payroll.component.html',
  styleUrls: ['./add-person-to-payroll.component.scss'],
  standalone: false
})
export class AddPersonToPayrollComponent implements OnInit {
  @Input() employeeStatuses: Enum[];
  @ViewChild('modalContent', { static: true })
  public modalContent: TemplateRef<any>;
  public modalRef: NgbModalRef;

  public clientCode: string;
  public data: KeyPair[] = [];
  public disableSave = true;
  public keyword = 'Value';
  public query: string;
  public saveText = 'Add Person';
  public selPerson = null;
  public payrollRoute: string;
  public seq: number;
  public year: number;

  private openModalSub: Subscription;
  private personIdsInPayroll: number[] = [];

  constructor(
    private ngModalService: NgbModal,
    private payrollMgmt: PayrollManagement,
    private payrollService: PayrollService,
    private peopleService: PeopleService,
    private route: ActivatedRoute,
    private router: Router,
    private sharedService: SharedService
  ) {
    this._searchPerson = _.debounce(this._searchPerson, 250);
  }

  ngOnInit(): void {
    this.clientCode = this.route.snapshot.paramMap.get('client_code');
    this.year = +this.route.snapshot.paramMap.get('year');
    this.seq = +this.route.snapshot.paramMap.get('seqNo');

    this.payrollRoute = `/client/${this.clientCode}/payroll/${this.year}/${this.seq}`;

    this.openModalSub = this.sharedService.openAddPersonToPayrollModal.subscribe(personIds => {
      this.personIdsInPayroll = personIds;
      this._openModal();
    });
  }

  ngOnDestroy(): void {
    this.openModalSub.unsubscribe();
  }

  public addNewPerson(modal: any) {
    modal.dismiss();
    // when adding a new person to the active payroll
    // the cached employee pays should be cleared
    // so the next time the payroll grid is rendered,
    // the new employee pay record will be fetched from the API
    this.payrollMgmt.clearCachedPayroll(this.clientCode, this.year, this.seq);

    this.router.navigate([`/client/${this.clientCode}/person/add`], {
      queryParams: { addToPayroll: this.payrollRoute }
    });
  }

  public dismiss(clearStatusInfo: boolean) {
    // task 9742
    // when the add person modal closes, clear the
    // shared payroll info. it gets populated when
    // the modal opens.
    // ----
    // bug 9860: this also fires when the user navigates away
    // to the create person screen and this modal is dismissed.
    // in that case, DO NOT clear the payroll status info,
    // as it is needed on that screen
    if (clearStatusInfo) {
      this.sharedService.setPayrollStatusInfo(null);
    }
  }

  public onChangeSearch($event) {
    // set button back to it's default state
    this.saveText = 'Add Person';
    this.disableSave = true;
    this.query = $event;

    if (!$event || !$event.trim() || $event.trim().length < 2) {
      this.data = [];
    } else {
      this._searchPerson($event.trim());
    }
  }

  public save() {
    if (this.personIdsInPayroll.includes(this.selPerson.Id)) {
      // fire event to focus on the first payline for this person
      this.sharedService.focusPayline.next(this.selPerson.Id);
    } else {
      // fire the Add Person to Payroll API endpoint
      this.payrollService
        .addPerson(this.clientCode, this.year, this.seq, this.selPerson.Id)
        .subscribe(resp => {
          // update the ag grid row model to insert the new payline
          this.sharedService.addPersonToPayroll.next(resp.Model);
        });
    }
  }

  public selectEvent(person) {
    // user has picked a person. if this person is already in payroll,
    // change the save text accordinly. enable the save button
    this.disableSave = false;
    this.selPerson = person;
    this.saveText = this.personIdsInPayroll.includes(person.Id) ? 'View Payline(s)' : 'Add Person';
  }

  // #region  private methods

  private _openModal() {
    // reset modal defaults
    this._resetModal();

    this.modalRef = this.ngModalService.open(this.modalContent, {
      size: 'md',
      backdrop: 'static',
      keyboard: false
    });

    this.modalRef.shown.subscribe(() => {
      // modal transition is complete
      document.getElementById('add-person-autocomplete')?.querySelector('input')?.focus();
    });

    this.modalRef.closed.subscribe(() => {
      // modal closed event has completed
      this.save();
    });
  }

  private _resetModal() {
    this.saveText = 'Add Person';
    this.disableSave = true;
    this.query = '';
    this.data = [];
  }

  private _searchPerson(query: string) {
    // showing the top 100 results here. if pagination is needed, update the logic here
    this.peopleService
      .searchPeople(this.clientCode, { query: query, perPage: 100 })
      .subscribe(results => {
        this.data = [];
        if (!results.Items || !results.Items.length) {
          return;
        }

        results.Items.forEach(item => {
          const middle = `${item.MiddleName?.trim() ? ` ${item.MiddleName.trim()} ` : ' '}`;
          const name = `${item.FirstName}${middle}${item.LastName}`;
          const status = `${this.employeeStatuses[item.CurrentStatus.StatusId].Value}`;
          const statusDate = `${new Date(
            item.CurrentStatus.DateStatusChange
          ).toLocaleDateString()}`;

          this.data.push({
            Id: item.Id,
            Value: `${item.EmployeeId} - ${name} - ${status} since ${statusDate}`
          });
        });
      });
  }

  // #endregion
}
